<template>
  <div class="ds-page" v-if="!$store.getters.isForbidden">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Benefits</div>
          <h2 class="page-title">{{ $route.name }} List</h2>
          <p class="page-subtitle">
            Manage core platform features, operational availability, versioning cycles, and global
            maintenance states.
          </p>
        </div>
        <div class="col-auto ms-auto">
          <div class="cc-page__header-actions">
            <button class="ds-btn ds-btn--blue" @click="handlerBtnCreate">
              <font-awesome-icon icon="plus" />
              Add MyBenefit
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-table-wrap">
        <table class="ds-table">
          <thead>
            <tr>
              <th>Feature Name</th>
              <th>Version</th>
              <th>Status</th>
              <th class="text-center">Is New</th>
              <th class="text-center">Maintenance</th>
              <th class="text-center">Is Coming</th>
              <th>Last Updated At</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in benefit_list_feature" :key="index">
              <td>
                <div class="cc-page__identity">
                  <span class="cc-page__identity-name">{{ item.feature_name || '—' }}</span>
                </div>
              </td>

              <td>
                <span class="badge bg-light text-dark font-monospace px-2 py-1">
                  {{ item.feature_version || 'v1.0.0' }}
                </span>
              </td>

              <td>
                <span class="ds-pill" :class="item.feature_status_color">
                  {{ item.feature_status_str }}
                </span>
              </td>

              <td class="text-center">
                <span class="ds-pill" :class="item.feature_is_new_color || 'ds-pill--secondary'">
                  {{ item.feature_is_new_str }}
                </span>
              </td>

              <td class="text-center">
                <span
                  class="ds-pill"
                  :class="item.feature_is_maintenance_color || 'ds-pill--secondary'"
                >
                  {{ item.feature_is_maintenance_str }}
                </span>
              </td>

              <td class="text-center">
                <span class="ds-pill" :class="item.feature_is_coming_color || 'ds-pill--secondary'">
                  {{ item.feature_is_coming_str }}
                </span>
              </td>

              <td>
                <span class="text-muted" style="font-size: 13px">{{ item.created_at || '—' }}</span>
              </td>

              <td class="text-center">
                <el-dropdown
                  trigger="hover"
                  @command="handleActionCommand($event, item)"
                  popper-class="cc-page__action-popper"
                >
                  <button class="ds-btn ds-btn--secondary ds-btn--sm cc-page__action-trigger">
                    Actions <i class="el-icon-arrow-down el-icon--right"></i>
                  </button>
                  <el-dropdown-menu slot="dropdown" class="cc-page__action-menu">
                    <el-dropdown-item command="edit">
                      <div class="cc-page__action-item">
                        <span class="cc-page__action-icon"
                          ><font-awesome-icon :icon="['fas', 'pen']"
                        /></span>
                        <span> Edit MyBen</span>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="status">
                      <div class="cc-page__action-item">
                        <span class="cc-page__action-icon"
                          ><font-awesome-icon :icon="['fas', 'sliders-h']"
                        /></span>
                        <span> Status MyBen</span>
                      </div>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </td>
            </tr>
          </tbody>
        </table>
        <el-empty
          v-if="total_rows == 0"
          description="No data found"
          :image="emptyStateImage"
        ></el-empty>
      </div>
      <pagination
        :page="page"
        :per_page="per_page"
        :list="benefit_list_feature"
        :total_rows="total_rows"
        @pageChange="pageChangeHandler"
      />
    </div>
    <b-modal v-model="isShownModal" size="xl" :title="modalTitle" hide-footer @hide="onHide">
      <div class="company-client-modal">
        <div class="company-client-modal__intro">
          <div class="company-client-modal__eyebrow">My Benefit Configuration</div>
          <div class="company-client-modal__lede">
            {{
              mode === 'status'
                ? 'Quickly toggle the activation status for this specific reward module feature.'
                : 'Manage dynamic configuration assets, deep-linking routing paths, and placement sorting order.'
            }}
          </div>
        </div>

        <b-form @submit.prevent="handleFormSubmit" v-loading="loading.detail">
          <template v-if="mode === 'status'">
            <section class="company-client-modal__section">
              <div class="company-client-modal__section-head">
                <h4>Status Control</h4>
                <p>Modify core availability for the selected feature component.</p>
              </div>
              <div class="company-client-modal__grid">
                <div class="ds-field">
                  <label class="ds-field__label">Feature Name</label>
                  <b-input
                    v-model="activeData.feature_name"
                    disabled
                    placeholder="Feature name"
                    type="text"
                  />
                </div>
                <div class="ds-field">
                  <label class="ds-field__label">Status</label>
                  <el-select
                    class="w-full"
                    size="small"
                    v-model="activeData.feature_status"
                    placeholder="Select Status"
                  >
                    <el-option
                      v-for="(item, index) in status_feature_options"
                      :key="index"
                      :value="item.value"
                      :label="item.text || item.label"
                    />
                  </el-select>
                </div>
              </div>
            </section>
          </template>

          <template v-else>
            <section class="company-client-modal__section">
              <div class="company-client-modal__section-head">
                <h4>Profile & Asset</h4>
                <p>Basic naming conventions and visual image file allocations.</p>
              </div>
              <div class="company-client-modal__grid">
                <div class="ds-field">
                  <label class="ds-field__label">Feature Name</label>
                  <b-input v-model="activeData.feature_name" placeholder="Input Name" type="text" />
                </div>

                <div class="ds-field">
                  <label class="ds-field__label">Feature Color</label>
                  <el-select
                    class="w-full"
                    size="small"
                    v-model="activeData.feature_color"
                    placeholder="Select Color"
                  >
                    <el-option
                      v-for="(item, index) in status_color_options"
                      :key="index"
                      :value="item.value"
                      :label="item.text"
                    />
                  </el-select>
                </div>

                <div class="ds-field company-client-modal__field-full">
                  <label class="ds-field__label">Feature Asset</label>
                  <b-form-file
                    v-model="form.feature_asset"
                    :state="Boolean(form.feature_asset)"
                    class="border-1 p-2 w-full"
                    accept=".png, .jpg, .jpeg"
                    @input="onChangeFile($event, 'feature_asset')"
                    placeholder="Choose a file or drop it here..."
                    drop-placeholder="Drop file here..."
                  />
                  <div class="d-flex align-items-center justify-content-between mt-2">
                    <small class="text-warning"
                      ><i>*Submit an image before creating a myben</i></small
                    >
                    <el-button @click="uploadImage" type="primary" size="mini" plain round
                      >Submit Image</el-button
                    >
                  </div>
                </div>

                <div
                  class="ds-field company-client-modal__field-full"
                  v-if="mode === 'edit' && activeData.feature_asset"
                >
                  <label class="ds-field__label">Current Image Preview</label>
                  <div class="company-client-modal__logo-preview">
                    <img :src="activeData.feature_asset" alt="Feature asset illustration" />
                  </div>
                </div>
              </div>
            </section>

            <section class="company-client-modal__section">
              <div class="company-client-modal__section-head">
                <h4>System State & Routing</h4>
                <p>Determine routing link targets and specific system operational modes.</p>
              </div>
              <div class="company-client-modal__grid">
                <div class="ds-field company-client-modal__field-full">
                  <label class="ds-field__label">Deeplink URL</label>
                  <b-input
                    v-model="activeData.feature_deeplink"
                    placeholder="Input Deeplink"
                    type="text"
                  />
                </div>

                <div class="ds-field">
                  <label class="ds-field__label">Is New</label>
                  <el-select
                    class="w-full"
                    size="small"
                    v-model="activeData.feature_is_new"
                    placeholder="Is new?"
                  >
                    <el-option
                      v-for="(item, index) in feature_is_new_options"
                      :key="index"
                      :value="item.value"
                      :label="item.text"
                    />
                  </el-select>
                </div>

                <div class="ds-field">
                  <label class="ds-field__label">Is Maintenance</label>
                  <el-select
                    class="w-full"
                    size="small"
                    v-model="activeData.feature_is_maintenance"
                    placeholder="Select maintenance"
                  >
                    <el-option
                      v-for="(item, index) in feature_is_maintenance_options"
                      :key="index"
                      :value="item.value"
                      :label="item.text"
                    />
                  </el-select>
                </div>

                <div class="ds-field">
                  <label class="ds-field__label">Is Coming Soon</label>
                  <el-select
                    class="w-full"
                    size="small"
                    v-model="activeData.feature_is_coming"
                    placeholder="Is coming?"
                  >
                    <el-option
                      v-for="(item, index) in feature_is_new_options"
                      :key="index"
                      :value="item.value"
                      :label="item.text"
                    />
                  </el-select>
                </div>

                <div class="ds-field">
                  <label class="ds-field__label">Global Status</label>
                  <el-select
                    class="w-full"
                    size="small"
                    v-model="activeData.feature_status"
                    placeholder="Select Status"
                  >
                    <el-option
                      v-for="(item, index) in status_feature_options"
                      :key="index"
                      :value="item.value"
                      :label="item.text"
                    />
                  </el-select>
                </div>
              </div>
            </section>

            <section class="company-client-modal__section">
              <div class="company-client-modal__section-head">
                <h4>Architecture & Categorization</h4>
                <p>Target framework platform build version parameters and sorting priorities.</p>
              </div>
              <div class="company-client-modal__grid">
                <div class="ds-field">
                  <label class="ds-field__label">Platform Device</label>
                  <el-select
                    class="w-full"
                    size="small"
                    v-model="activeData.feature_platform"
                    placeholder="Select Platform"
                  >
                    <el-option
                      v-for="(item, index) in platform_feature_options"
                      :key="index"
                      :value="item.value"
                      :label="item.text"
                    />
                  </el-select>
                </div>

                <div class="ds-field">
                  <label class="ds-field__label">Sort Order Priority</label>
                  <el-select
                    class="w-full"
                    size="small"
                    v-model="activeData.feature_sort_order"
                    placeholder="Select Sort Order"
                  >
                    <el-option
                      v-for="(item, index) in feature_sort_oder_option"
                      :key="index"
                      :value="item.value"
                      :label="item.text"
                    />
                  </el-select>
                </div>

                <div class="ds-field">
                  <label class="ds-field__label">Feature Category</label>
                  <el-select
                    class="w-full"
                    size="small"
                    v-model="activeData.feature_category"
                    placeholder="Select Category"
                  >
                    <el-option
                      v-for="(item, index) in category_feature_options"
                      :key="index"
                      :value="item.value"
                      :label="item.text"
                    />
                  </el-select>
                </div>

                <div class="ds-field">
                  <label class="ds-field__label">System Version Code</label>
                  <el-select
                    class="w-full"
                    size="small"
                    v-model="activeData.feature_version"
                    placeholder="Select version"
                  >
                    <el-option
                      v-for="(item, index) in version_feature_options"
                      :key="index"
                      :value="item.value"
                      :label="item.text"
                    />
                  </el-select>
                </div>
              </div>
            </section>

            <section class="company-client-modal__section">
              <div class="company-client-modal__section-head">
                <h4>Terms, Conditions</h4>
                <p>Provide policy documentation details and distribution text templates.</p>
              </div>

              <div class="company-client-modal__grid">
                <div class="ds-field company-client-modal__field-full">
                  <label class="ds-field__label">Description</label>
                  <div class="editor-wrapper" style="margin-bottom: 50px">
                    <quill-editor
                      v-model="activeData.feature_description"
                      ref="tnc"
                      :options="editorOption"
                      style="height: 130px"
                    />
                  </div>
                </div>
                <br />

                <div class="ds-field company-client-modal__field-full">
                  <label class="ds-field__label">Term and Condition</label>
                  <div class="editor-wrapper" style="margin-bottom: 50px">
                    <quill-editor
                      v-model="activeData.feature_tnc"
                      ref="tnc"
                      :options="editorOption"
                      style="height: 130px"
                    />
                  </div>
                </div>
                <br />

                <div class="ds-field company-client-modal__field-full">
                  <label class="ds-field__label">Info Meta</label>
                  <div class="editor-wrapper" style="margin-bottom: 50px">
                    <quill-editor
                      v-model="activeData.feature_info"
                      ref="tnc"
                      :options="editorOption"
                      style="height: 130px"
                    />
                  </div>
                </div>
                <br />
              </div>
            </section>
          </template>
        </b-form>

        <div class="company-client-modal__actions">
          <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="onHide">Cancel</button>
          <button
            class="ds-btn ds-btn--primary ds-btn--sm"
            :disabled="loading.submit"
            @click="handleFormSubmit"
          >
            Submit Configuration
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>
<script>
import emptyStateImage from '@/assets/images/undraw/empty-approval-history.svg';
import Pagination from '../components/Pagination.vue';
import popupErrorMessages from '@/library/popup-error-messages';
import {
  GET_MY_BENEFIT_LIST,
  CREATE_MYBEN,
  UPDATE_MY_BENEFIT_V2,
  UPDATE_STATUS_MYBENEFIT,
} from '@/store/modules/companies';
import base64File from '@/library/base64File';
import imageCompress from '@/library/vue-image-compression';
import { UPLOAD_IMAGE } from '@/store/modules/channel';
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
export default {
  components: { Pagination, quillEditor },
  name: 'MyBenFeature',
  metaInfo: {
    title: 'MyBenefit Feat',
  },
  data() {
    return {
      editorOption: {
        placeholder: '1. Test ...',
      },
      isShownModalEdit: false,
      isShownModalStatusEdit: false,
      loading: {
        data: false,
        benefit_list_feature: false,
      },
      emptyStateImage,
      total_rows: 0,
      list: [],
      isShownModalMyBenefit: false,
      page: 1,
      per_page: 10,
      benefit_list_feature: [],
      tempTarget: {},
      // target_list: [],
      selected_file: {
        file_image: null,
        file_image_base64: '',
      },
      selected_item: {
        image_url: '',
        file_image: null,
        file_image_base64: '',
      },
      image: null,
      image_base64: null,
      upload: {
        image: null,
      },
      file_tmp: '',
      form: {
        feature_name: '',
        image: null,
        // feature_asset: 'https://fastly.picsum.photos/id/21/600/200.jpg?hmac=vGZTc0ZN2Iz_A06wnsON4W1aW3LPFcB39ZG-6ojg_ws',
        feature_asset: '',
        feature_color: '',
        feature_deeplink: '',
        feature_is_new: '',
        feature_is_maintenance: '',
        feature_is_coming: '',
        feature_status: '',
        feature_description: '',
        feature_info: '',
        feature_tnc: '',
        feature_platform: '',
        feature_sort_order: '',
        feature_category: '',
        feature_version: '',
      },
      data_image: '',
      selected_item: {
        feature_name: '',
        // feature_asset: 'https://fastly.picsum.photos/id/21/600/200.jpg?hmac=vGZTc0ZN2Iz_A06wnsON4W1aW3LPFcB39ZG-6ojg_ws',
        feature_asset: '',
        feature_color: '',
        feature_deeplink: '',
        feature_is_new: '',
        feature_is_coming: '',
        feature_is_maintenance: '',
        feature_status: '',
        feature_description: '',
        feature_info: '',
        feature_tnc: '',
        feature_platform: '',
        feature_sort_order: '',
        feature_category: '',
        feature_version: '',
        feature_id: '',
      },
      filter: {
        search_by: 'company_id',
        search_keyword: '',
        status_filter: '',
      },
      search_options: [
        {
          text: 'Company',
          value: 'company_id',
        },
        {
          text: 'MyBenefit Package',
          value: 'mybenefit_package_id',
        },
      ],
      company_list: [
        {
          value: '',
          text: 'All',
        },
      ],
      package_list: [
        {
          value: '',
          text: 'All',
        },
      ],
      status_options: [
        { value: '', text: 'All' },
        { value: 'request', text: 'Waiting Approval' },
        { value: 'approved', text: 'Approved' },
        { value: 'rejected', text: 'Rejected' },
      ],
      status_color_options: [
        { value: 'gradientBlue', text: 'Gradient Blue' },
        { value: 'gradientYellow', text: 'Gradient Yellow ' },
        { value: 'gradientBlack', text: 'Gradient Black ' },
        { value: 'gradientPurple', text: 'Gradient Purple ' },
        { value: 'gradientGreen', text: 'Gradient Green' },
        { value: 'gradientRed', text: 'Gradient Red' },
        { value: 'gradientCyan', text: 'Gradient Cyan' },
        { value: 'transparent', text: 'Transparent ' },
      ],
      status_feature_options: [
        { value: 'inactive', text: 'Inactive' },
        { value: 'active', text: 'Active ' },
        { value: 'maintenance', text: 'Maintenance ' },
        { value: 'closed', text: 'Closed ' },
        // { value: 'is_coming', text: 'Coming Soon' },
      ],
      platform_feature_options: [
        { value: 'both', text: 'Both' },
        { value: 'web', text: 'Web ' },
        { value: 'mobile', text: 'Mobile ' },
      ],
      category_feature_options: [
        { value: 'unnamed', text: 'Unnamed' },
        { value: 'product', text: 'Product ' },
        { value: 'tool', text: 'Tool ' },
      ],
      version_feature_options: [
        { value: 'v1', text: 'V1' },
        { value: 'v2', text: 'V2 ' },
      ],
      feature_is_maintenance_options: [
        { value: true, text: 'Active' },
        { value: false, text: 'Inactive ' },
      ],
      feature_is_new_options: [
        { value: true, text: 'True' },
        { value: false, text: 'False ' },
      ],
      feature_sort_oder_option: [
        { value: 1, text: '1' },
        { value: 2, text: '2 ' },
      ],
      timeout: {
        filter_company: null,
      },
    };
  },
  async mounted() {
    this.loadMyBenefitListRevamp();
  },
  computed: {
    // 1. Mengontrol visibilitas satu pintu modal tunggal
    isShownModal: {
      get() {
        return this.isShownModalMyBenefit || this.isShownModalEdit || this.isShownModalStatusEdit;
      },
      set(val) {
        if (!val) this.onHide();
      },
    },
    // 2. Mendeteksi mode aksi aktif saat ini
    mode() {
      if (this.isShownModalMyBenefit) return 'create';
      if (this.isShownModalEdit) return 'edit';
      if (this.isShownModalStatusEdit) return 'status';
      return '';
    },
    // 3. Menampilkan teks judul modal secara dinamis
    modalTitle() {
      if (this.mode === 'create') return 'New Benefit';
      if (this.mode === 'edit') return 'Edit My Benefit';
      if (this.mode === 'status') return `Update Status #${this.selected_item?.feature_id}`;
      return '';
    },
    // 4. Mencegah bentrokan data object target antara create form vs edit form
    activeData() {
      return this.mode === 'create' ? this.form : this.selected_item;
    },
  },
  methods: {
    handleActionCommand(command, item) {
      if (command === 'edit') {
        this.handlerBtnEdit(item);
      } else if (command === 'status') {
        this.handlerBtnStatus(item);
      }
    },
    handleFormSubmit() {
      if (this.mode === 'create') return this.onSubmit();
      if (this.mode === 'edit') return this.doUpdate();
      if (this.mode === 'status') return this.doUpdateStatus();
    },
    async uploadImage() {
      this.company_id = parseInt(this.company_id);
      try {
        const requestBody = {
          file_data: this.file_tmp,
          filename: 'image-avatar',
          source: 'vdashboard',
          product: 'avatar',
          category: 'system',
        };
        const response = await this.$store.dispatch(UPLOAD_IMAGE, {
          data: requestBody,
        });
        const { file_id, file_url } = response.data.rows;
        this.data_image = { file_id, file_url };
        localStorage.setItem('uploadedImage', JSON.stringify(this.data_image));
        this.file_tmp = '';
        this.$message({
          title: 'Success',
          type: 'success',
          message: 'Upload success created',
        });

        this.getList();
      } catch (err) {
        console.error('Upload Image Error:', err.response?.data || err.message);
        popupErrorMessages(err.response?.data);
      }
    },
    async doUpdate() {
      this.$confirm(
        'You will update the myben. Make sure you have input the data correctly. Continue?',
        'Confirmation',
        {
          type: 'info',
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              done();
              const storedImage = localStorage.getItem('uploadedImage');
              const uploadedImage = storedImage ? JSON.parse(storedImage) : null;
              const requestBody = {
                feature_name: this.selected_item.feature_name,
                // feature_asset: this.selected_item.feature_asset,
                feature_asset: uploadedImage?.file_url,
                feature_color: this.selected_item.feature_color,
                feature_deeplink: this.selected_item.feature_deeplink,
                feature_is_new: this.selected_item.feature_is_new,
                feature_is_maintenance: this.selected_item.feature_is_maintenance,
                feature_is_coming: this.selected_item.feature_is_coming,
                feature_status: this.selected_item.feature_status,
                feature_description: this.selected_item.feature_description,
                feature_info: this.selected_item.feature_info,
                feature_tnc: this.selected_item.feature_tnc,
                feature_platform: this.selected_item.feature_platform,
                feature_sort_order: this.selected_item.feature_sort_order,
                feature_category: this.selected_item.feature_category,
                feature_version: this.selected_item.feature_version,
              };
              const id = this.selected_item.feature_id;
              await this.$store
                .dispatch(UPDATE_MY_BENEFIT_V2, { id, data: requestBody })
                .then(() => {
                  this.$message({
                    title: 'Success',
                    type: 'success',
                    message: 'MyBenefit updated',
                  });
                  this.loadMyBenefitListRevamp();
                  this.onHide();
                })
                .catch((err) => {
                  popupErrorMessages(err.response.data);
                });
              return;
            }
            done();
          },
        }
      ).catch(() => {});
    },
    onHide() {
      this.isShownModalStatusEdit = false;
      this.isShownModalEdit = false;
      this.isShownModalMyBenefit = false;
    },
    handlerBtnStatus(item) {
      this.selected_item = item;
      this.isShownModalStatusEdit = true;
    },
    async doUpdateStatus() {
      this.$confirm(
        'You will update status of my benefit. Make sure you have input the data correctly. Continue?',
        'Confirmation',
        {
          type: 'info',
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              done();
              const requestBody = {
                feature_status: this.selected_item.feature_status,
              };
              const id = this.selected_item.feature_id;
              try {
                const response = await this.$store.dispatch(UPDATE_STATUS_MYBENEFIT, {
                  id,
                  data: requestBody,
                });
                this.$message({
                  title: 'Success',
                  type: 'success',
                  message: 'Status MyBen updated',
                });
                this.loadMyBenefitListRevamp();
                this.onHide();
              } catch (err) {
                popupErrorMessages(err?.response?.data || err.message || 'Something went wrong');
              }
              return;
            }
            done();
          },
        }
      ).catch(() => {});
    },
    handlerBtnEdit(item) {
      this.selected_item = item;
      this.isShownModalEdit = true;
    },
    handlerBtnCreate() {
      this.isShownModalMyBenefit = true;
    },
    async loadMyBenefitListRevamp() {
      this.loading.benefit_list_feature = true;
      const payload = {
        page: this.page,
        per_page: this.per_page,
        order_by: 'updated_at,desc',
      };
      const response = await this.$store.dispatch(GET_MY_BENEFIT_LIST, payload).catch(() => null);

      const rows = response?.data?.rows || [];
      const count = response?.data?.count || 0;

      this.total_rows = count;
      this.benefit_list_feature = rows;
      this.loading.benefit_list_feature = false;
    },
    async onChangeFile(event, target) {
      if (this.form[target]) {
        const compressed = await imageCompress(this.form[target]);
        this.form[`${target}_base64`] = await base64File(compressed);
        this.file_tmp = this.form[`${target}_base64`];
      }
    },
    async onSubmit() {
      const storedImage = localStorage.getItem('uploadedImage');
      const uploadedImage = storedImage ? JSON.parse(storedImage) : null;
      try {
        const requestBody = {
          feature_name: this.form.feature_name,
          feature_asset: uploadedImage?.file_url,
          feature_color: this.form.feature_color,
          feature_deeplink: this.form.feature_deeplink,
          feature_is_new: this.form.feature_is_new,
          feature_is_maintenance: this.form.feature_is_maintenance,
          feature_is_coming: this.form.feature_is_coming,
          feature_status: this.form.feature_status,
          feature_description: this.feature_description,
          feature_info: this.form.feature_info,
          feature_tnc: this.form.feature_tnc,
          feature_platform: this.form.feature_platform,
          feature_sort_order: this.form.feature_sort_order,
          feature_category: this.form.feature_category,
          feature_version: this.form.feature_version,
        };
        const response = await this.$store.dispatch(CREATE_MYBEN, requestBody);
        this.form.feature_name = '';
        this.form.feature_asset = '';
        this.form.feature_color = '';
        this.form.feature_deeplink = '';
        (this.form.feature_is_new = ''),
          (this.form.feature_is_maintenance = ''),
          (this.form.feature_is_coming = ''),
          (this.form.feature_status = ''),
          (this.form.feature_description = ''),
          (this.form.feature_info = ''),
          (this.form.feature_tnc = ''),
          (this.form.feature_platform = ''),
          (this.form.feature_sort_order = ''),
          (this.form.feature_category = '');
        this.form.feature_version = '';
        this.benefit_list_feature = response;
        this.$message({
          title: 'Success',
          type: 'success',
          message: 'MyBenefit successfully created',
        });
        this.onHide();
        this.loadMyBenefitListRevamp();
      } catch (err) {
        popupErrorMessages(err.response.data);
      }
    },
    pageChangeHandler(page) {
      this.page = page;
      this.loadMyBenefitListRevamp();
    },
    searchHandler() {
      this.page = 1;
      this.getCompanyRequestor();
      this.loadMyBenefitListRevamp();
    },
  },
};
</script>
<style>
.tag-gold {
  background-color: #fcc200;
  color: #fff;
  font-weight: 600;
}
.tag-bronze {
  background-color: #cd7f32;
  color: #fff;
  font-weight: 600;
}

.company-client-modal__intro {
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(32, 107, 196, 0.08), rgba(32, 107, 196, 0.04));
}

.company-client-modal__eyebrow {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #206bc4;
  margin-bottom: 6px;
}

.company-client-modal__lede {
  font-size: 13px;
  color: #4b5563;
}

.company-client-modal__section + .company-client-modal__section {
  margin-top: 18px;
}

.company-client-modal__section-head {
  margin-bottom: 12px;
}

.company-client-modal__section-head h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #182433;
}

.company-client-modal__section-head p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #656d77;
}

.company-client-modal__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.company-client-modal__field-full {
  grid-column: 1 / -1;
}

.company-client-modal__logo-preview {
  margin-top: 12px;
  padding: 12px;
  border: 1px dashed #dce1e7;
  border-radius: 12px;
  background: #f8fafc;
  display: flex;
  justify-content: center;
}

.company-client-modal__logo-preview img {
  max-width: 100%;
  max-height: 120px;
  object-fit: contain;
}

.company-client-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 22px;
}

/* Base custom field definition structure */
.ds-field {
  display: flex;
  flex-direction: column;
}

.ds-field__label {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 6px;
}

@media (max-width: 768px) {
  .company-client-modal__grid {
    grid-template-columns: 1fr;
  }
}
</style>
